import { z } from 'zod';
import type { BotSlot } from '../bot/types';

export const DAY_NAMES = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
] as const;
export const DAY_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'] as const;

const hhmm = z.string().regex(/^\d{2}:\d{2}$/, 'Hora en formato HH:MM');
const minutesOf = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

export const timeRangeSchema = z
  .object({ start: hhmm, end: hhmm })
  .refine(
    (r) => minutesOf(r.start) < minutesOf(r.end),
    'La hora de fin debe ser mayor que la de inicio',
  );
export type TimeRange = z.infer<typeof timeRangeSchema>;

/** Horario semanal: por día (0 = domingo … 6 = sábado) los rangos en que atiende; sin clave o vacío = no atiende. */
export const weeklyScheduleSchema = z.record(
  z.string().regex(/^[0-6]$/),
  z.array(timeRangeSchema).max(3),
);
export type WeeklySchedule = z.infer<typeof weeklyScheduleSchema>;

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha en formato AAAA-MM-DD');
/** Días en que no atiende (descanso, vacaciones, capacitación). */
export const scheduleExceptionSchema = z
  .object({
    from: ymd,
    to: ymd,
    reason: z
      .string()
      .trim()
      .max(120)
      .optional()
      .nullable()
      .transform((v) => (v ? v : null)),
  })
  .refine((e) => e.from <= e.to, 'La fecha de fin debe ser igual o posterior a la de inicio');
export type ScheduleException = z.infer<typeof scheduleExceptionSchema>;

/** Turno de un día concreto: horas de ese día o, sin rangos, día libre. Manda sobre el patrón semanal y los descansos. */
export const dayOverrideSchema = z.object({
  date: ymd,
  ranges: z.array(timeRangeSchema).max(3),
});
export type DayOverride = z.infer<typeof dayOverrideSchema>;

export const advisorScheduleInputSchema = z.object({
  companyId: z.string().uuid(),
  userId: z.string().uuid(),
  weekly: weeklyScheduleSchema.default({}),
  exceptions: z.array(scheduleExceptionSchema).max(50).default([]),
  /** Si no viene, se conservan los turnos del mes ya cargados. */
  overrides: z.array(dayOverrideSchema).max(400).optional(),
});
export type AdvisorScheduleInput = z.infer<typeof advisorScheduleInputSchema>;
export type AdvisorSchedulePayload = z.input<typeof advisorScheduleInputSchema>;

/** Horario de la sala de ventas de un proyecto (misma forma que el de la empresa). null = el de la empresa. */
export const salesRoomHoursSchema = z.object({
  start: hhmm,
  end: hhmm,
  days: z.array(z.number().int().min(0).max(6)).min(1),
});
export type SalesRoomHours = z.infer<typeof salesRoomHoursSchema>;

export interface AdvisorScheduleDto {
  userId: string;
  fullName: string;
  role: string;
  weekly: WeeklySchedule;
  exceptions: ScheduleException[];
  overrides: DayOverride[];
  /** false = nunca se cargó (el bot usa el horario de la sala). */
  configured: boolean;
  updatedAt: string | null;
}

export const availabilityQuerySchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid(),
  advisorId: z.string().uuid().optional(),
  count: z.coerce.number().int().min(1).max(5).default(2),
});

export interface AvailabilityDto {
  slots: BotSlot[];
  /** Asesor con el que se ofrecen los horarios (el del lead, o uno en turno si el del lead no tiene espacio en 3 días). */
  advisorId: string | null;
  advisorName: string | null;
  /** true = no es el asesor pedido. */
  fallback: boolean;
  reason: string | null;
  salesRoom: SalesRoomHours;
}

export const availabilityCheckSchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
  advisorId: z.string().uuid().optional(),
  at: z.coerce.date(),
  minutes: z.coerce.number().int().min(15).max(240).optional(),
  /** Cita que se está moviendo (no cuenta como choque consigo misma). */
  excludeInteractionId: z.string().uuid().optional(),
});
export type AvailabilityCheckInput = z.infer<typeof availabilityCheckSchema>;

export interface AvailabilityCheckDto {
  available: boolean;
  /** Avisos en español para la pantalla ("La sala está cerrada a esa hora", "Ya tiene una cita a esa hora"). */
  issues: string[];
}

/** Horario de la empresa o de la sala → horario semanal (un rango por día). */
export function weeklyFromHours(h: SalesRoomHours): WeeklySchedule {
  const out: WeeklySchedule = {};
  for (const d of h.days) out[String(d)] = [{ start: h.start, end: h.end }];
  return out;
}

/** "Lun a Vie 09:00–18:00 · Sáb 09:00–13:00" */
export function describeWeekly(weekly: WeeklySchedule): string {
  const parts: string[] = [];
  const order = [1, 2, 3, 4, 5, 6, 0];
  let i = 0;
  while (i < order.length) {
    const d = order[i]!;
    const ranges = weekly[String(d)] ?? [];
    if (ranges.length === 0) {
      i += 1;
      continue;
    }
    const key = ranges.map((r) => `${r.start}–${r.end}`).join(' y ');
    let j = i;
    while (
      j + 1 < order.length &&
      (weekly[String(order[j + 1]!)] ?? []).map((r) => `${r.start}–${r.end}`).join(' y ') === key
    )
      j += 1;
    parts.push(`${DAY_SHORT[d]}${j > i ? ` a ${DAY_SHORT[order[j]!]}` : ''} ${key}`);
    i = j + 1;
  }
  return parts.length ? parts.join(' · ') : 'Sin horario';
}

// ---------------------------------------------------------------- turnos del mes

const monthKeySchema = z.string().regex(/^\d{4}-\d{2}$/, 'Mes en formato AAAA-MM');

export const scheduleDaysInputSchema = z.object({
  companyId: z.string().uuid(),
  userId: z.string().uuid(),
  days: z.array(dayOverrideSchema).min(1).max(62),
  /** true = quitar el turno de esos días (vuelve a valer el patrón semanal). */
  clear: z.boolean().default(false),
});
export type ScheduleDaysInput = z.infer<typeof scheduleDaysInputSchema>;
export type ScheduleDaysPayload = z.input<typeof scheduleDaysInputSchema>;

export const copyMonthSchema = z
  .object({
    companyId: z.string().uuid(),
    from: monthKeySchema,
    to: monthKeySchema,
    /** true = solo la vista previa (cuántos días y asesores se copiarían y cuántos turnos pierde el destino). */
    dryRun: z.boolean().default(false),
  })
  .refine((v) => v.from !== v.to, 'Elige dos meses distintos');
export type CopyMonthInput = z.infer<typeof copyMonthSchema>;
export type CopyMonthPayload = z.input<typeof copyMonthSchema>;

/** Resultado de copiar un mes (o de su vista previa): `lost` = turnos que el destino pierde. */
export interface CopyMonthResult {
  advisors: number;
  days: number;
  lost: number;
}

/** Quitar todos los turnos cargados de un mes (todos los asesores de la empresa) en una sola llamada. */
export const clearMonthSchema = z.object({ companyId: z.string().uuid(), month: monthKeySchema });
export type ClearMonthInput = z.infer<typeof clearMonthSchema>;

export const scheduleMonthQuerySchema = z.object({
  companyId: z.string().uuid(),
  month: monthKeySchema,
});

/**
 * De dónde sale lo que vale un día: TURNO (turno del mes con horas), DESCANSO (día libre o descanso), PATRON
 * (patrón semanal), NINGUNO (no atiende) o SALA (sin patrón ni turnos en el mes: vale el horario de la sala).
 */
export type ScheduleCellSource = 'TURNO' | 'DESCANSO' | 'PATRON' | 'NINGUNO' | 'SALA';
export interface ScheduleCell {
  ranges: TimeRange[];
  source: ScheduleCellSource;
}
export interface ScheduleMonthDto {
  month: string;
  days: string[];
  items: Array<{
    userId: string;
    fullName: string;
    role: string;
    cells: Record<string, ScheduleCell>;
  }>;
}

/** Fechas (AAAA-MM-DD) de un mes. */
export function monthDays(month: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const n = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/** Día de la semana (0 = domingo) de una fecha AAAA-MM-DD. */
export function weekdayOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay();
}

/** ¿El patrón semanal tiene al menos un día con horas? */
export function hasSchedule(weekly: WeeklySchedule | null | undefined): boolean {
  return !!weekly && Object.values(weekly).some((r) => r.length > 0);
}

/** ¿Hay turnos cargados (con horas o libres) en el mes de la fecha? */
export function hasOverridesInMonth(
  overrides: DayOverride[] | null | undefined,
  date: string,
): boolean {
  const month = date.slice(0, 7);
  return !!overrides?.some((o) => o.date.startsWith(`${month}-`));
}

/**
 * Qué vale para un día (regla única, Lote 6 · I2): turno del mes > descanso > patrón semanal. Sin nada de eso,
 * el día es NINGUNO (no atiende) si el asesor tiene patrón semanal o turnos cargados en ese mes; solo sin patrón
 * ni turnos en el mes vale el horario de la sala (SALA). Así «en blanco» con turnos cargados = no atiende.
 */
export function effectiveRanges(
  date: string,
  weekly: WeeklySchedule | null | undefined,
  exceptions: ScheduleException[] | null | undefined,
  overrides: DayOverride[] | null | undefined,
): ScheduleCell {
  const o = overrides?.find((x) => x.date === date);
  if (o) return { ranges: o.ranges, source: o.ranges.length ? 'TURNO' : 'DESCANSO' };
  if (exceptions?.some((e) => e.from <= date && date <= e.to))
    return { ranges: [], source: 'DESCANSO' };
  const ranges = weekly?.[String(weekdayOf(date))] ?? [];
  if (ranges.length) return { ranges, source: 'PATRON' };
  if (hasSchedule(weekly) || hasOverridesInMonth(overrides, date))
    return { ranges: [], source: 'NINGUNO' };
  return { ranges: [], source: 'SALA' };
}

/** Semana del mes (desde 0) de una fecha, con semanas de lunes a domingo: la semana 0 es la que contiene el día 1. */
export function weekOfMonth(date: string): number {
  const day = Number(date.slice(8, 10));
  const offset = (weekdayOf(`${date.slice(0, 7)}-01`) + 6) % 7; // lunes = 0
  return Math.floor((day - 1 + offset) / 7);
}

/**
 * Copia de turnos por semana y día de la semana (Lote 6 · I2): el martes de la semana 2 del origen va al martes de
 * la semana 2 del destino. Devuelve null si ese día no existe en el destino (p. ej. el destino empieza más tarde
 * en la semana, o una quinta semana sin equivalente).
 */
export function copyDateByWeek(date: string, toMonth: string): string | null {
  const week = weekOfMonth(date);
  const weekday = (weekdayOf(date) + 6) % 7; // lunes = 0
  const offsetTo = (weekdayOf(`${toMonth}-01`) + 6) % 7;
  const day = week * 7 + weekday - offsetTo + 1;
  if (day < 1 || day > monthDays(toMonth).length) return null;
  return `${toMonth}-${String(day).padStart(2, '0')}`;
}

/** "10:00–19:00", "09:00–13:00 y 15:00–19:00" o "Libre". */
export function describeRanges(ranges: TimeRange[]): string {
  return ranges.length ? ranges.map((r) => `${r.start}–${r.end}`).join(' y ') : 'Libre';
}
